import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ChoiceHero from '../components/ChoiceHero';
import { useT } from '../i18n/LanguageContext';

const Home = () => {
  const t = useT();
  const pillars = t('home.pillars') || [];

  const caseMedia = [
    { image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80', category: 'WEDDINGS' },
    { image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80', category: 'MICE' },
    { image: 'https://images.unsplash.com/photo-1763231575952-98244918f99b?w=1200&q=80', category: 'EVENTS' },
  ];
  const caseCopy = t('home.cases') || [];
  const portfolioPreview = caseMedia.map((m, i) => ({ ...m, ...(caseCopy[i] || {}) }));

  const testimonialAuthors = [
    { author: 'Syed Lakthe Hassanain', company: 'Muslim Hands' },
    { author: 'Sebastian Schmid', company: 'Messe Stuttgart' },
    { author: 'Amila Salkovic', company: 'UNILINE PLUS DOO' },
  ];
  const testimonialCopy = t('home.testimonialItems') || [];
  const testimonials = testimonialAuthors.map((a, i) => ({ ...a, ...(testimonialCopy[i] || {}) }));

  const FILTERS = ['ALL', 'MICE', 'EVENTS', 'WEDDINGS'];
  const [activeFilter, setActiveFilter] = useState('ALL');
  const visibleCases =
    activeFilter === 'ALL'
      ? portfolioPreview
      : portfolioPreview.filter((p) => p.category === activeFilter);

  return (
    <div className="min-h-screen">
      <SEO
        title="GlowMeets - Premium Event Management in Türkiye"
        description="Luxury event planning in Istanbul, Antalya, and Trabzon. Corporate events, destination weddings, and unforgettable experiences crafted by experts."
        keywords="event management Turkey, destination weddings Istanbul, corporate events Antalya, luxury event planning, wedding planner Turkey, MICE Turkey"
        url="/"
      />
      {/* Hero, Three-Panel Choice Hero */}
      <ChoiceHero />

      {/* ─────────────────────────  RELATIONSHIPS  ───────────────────────── */}
      {/* Trusted By + Partners merged into one navy editorial section */}
      <section
        className="relative bg-navy text-bone overflow-hidden"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="section-relationships"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Eyebrow + headline */}
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '9pt',
                fontWeight: 600,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: '#D6A87A',
              }}
              className="mb-6"
              data-testid="eyebrow-relationships"
            >
              {t('home.relationships.eyebrow')}
            </div>
            <h2
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(34px, 3.8vw, 52px)',
                lineHeight: 1.1,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              {t('home.relationships.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('home.relationships.headline2')}
              </em>
            </h2>
          </div>

          {/* Trusted By marquee */}
          <div className="mb-12">
            <div
              className="text-center mb-6"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '9pt',
                fontWeight: 600,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: 'rgba(214,168,122,0.72)',
              }}
            >
              {t('home.relationships.trustedBy')}
            </div>
            <div className="relative overflow-hidden">
              <div
                className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(to right, #0A132F 0%, rgba(10,19,47,0) 100%)',
                }}
              />
              <div
                className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(to left, #0A132F 0%, rgba(10,19,47,0) 100%)',
                }}
              />
              <div className="flex animate-scroll">
                {[...Array(2)].map((_, dup) => (
                  <div key={dup} className="flex shrink-0">
                    {[
                      'Messe Stuttgart',
                      'Muslim Hands UK',
                      'Octapharma',
                      'Farmavita',
                      'Al Faris KSA',
                      'Uniline Plus',
                      'Trouw Nutrition',
                      'All Travel',
                    ].map((name) => (
                      <div
                        key={`${dup}-${name}`}
                        className="flex items-center justify-center px-12 whitespace-nowrap"
                        style={{
                          fontFamily: '"Cormorant Garamond", serif',
                          fontSize: '24px',
                          fontWeight: 500,
                          color: 'rgba(244,239,227,0.78)',
                          letterSpacing: '0.01em',
                          minWidth: '220px',
                          height: '64px',
                        }}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Copper hairline divider */}
          <div
            className="mx-auto my-12"
            style={{
              height: '1px',
              maxWidth: '880px',
              backgroundColor: 'rgba(214,168,122,0.35)',
            }}
          />

          {/* Partners marquee, reverse direction */}
          <div className="mt-12">
            <div
              className="text-center mb-6"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '9pt',
                fontWeight: 600,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: 'rgba(214,168,122,0.72)',
              }}
            >
              {t('home.relationships.partners')}
            </div>
            <div className="relative overflow-hidden">
              <div
                className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(to right, #0A132F 0%, rgba(10,19,47,0) 100%)',
                }}
              />
              <div
                className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(to left, #0A132F 0%, rgba(10,19,47,0) 100%)',
                }}
              />
              <div className="flex animate-scroll-reverse">
                {[...Array(2)].map((_, dup) => (
                  <div key={dup} className="flex shrink-0">
                    {[
                      'Four Seasons',
                      'Mandarin Oriental',
                      'Rixos Hotels',
                      'Raffles',
                      'Kempinski',
                      'St. Regis',
                      'Hilton',
                      'Radisson Blu',
                      'The Land of Legends',
                      'Limak Hotels',
                      'Concorde Deluxe',
                    ].map((name) => (
                      <div
                        key={`${dup}-${name}`}
                        className="flex items-center justify-center px-12 whitespace-nowrap"
                        style={{
                          fontFamily: '"Cormorant Garamond", serif',
                          fontSize: '24px',
                          fontWeight: 500,
                          color: 'rgba(244,239,227,0.78)',
                          letterSpacing: '0.01em',
                          minWidth: '220px',
                          height: '64px',
                        }}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────  DISCIPLINE / WHY  ───────────────────────── */}
      <section
        className="bg-bone"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="section-discipline"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* LEFT: Eyebrow + headline + body */}
            <div className="lg:col-span-5">
              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '9pt',
                  fontWeight: 600,
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                }}
                className="mb-6"
                data-testid="eyebrow-discipline"
              >
                {t('home.discipline.eyebrow')}
              </div>
              <h2
                className="text-ink"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: 'clamp(34px, 3.6vw, 50px)',
                  lineHeight: 1.08,
                  fontWeight: 500,
                  letterSpacing: '-0.005em',
                }}
              >
                {t('home.discipline.headline1')}{' '}
                <em
                  className="italic"
                  style={{ color: '#B98556', fontWeight: 400 }}
                >
                  {t('home.discipline.headline2')}
                </em>
              </h2>
              <p
                className="mt-8 max-w-md"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  fontWeight: 300,
                  lineHeight: 1.7,
                  color: '#4A4F63',
                }}
              >
                {t('home.discipline.intro')}
              </p>

              <div
                className="mt-10"
                style={{
                  height: '1px',
                  width: '88px',
                  backgroundColor: 'rgba(214,168,122,0.55)',
                }}
              />
            </div>

            {/* RIGHT: 2x2 numbered pillars */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-12 gap-x-10">
                {pillars.map((pillar, index) => (
                  <div
                    key={index}
                    className="relative pl-0"
                    data-testid={`pillar-${index + 1}`}
                  >
                    {/* Number */}
                    <div
                      style={{
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: '20px',
                        fontStyle: 'italic',
                        fontWeight: 400,
                        color: '#D6A87A',
                        letterSpacing: '0.05em',
                      }}
                      className="mb-3"
                    >
                      0{index + 1}
                    </div>

                    {/* Copper hairline */}
                    <div
                      style={{
                        height: '1px',
                        width: '40px',
                        backgroundColor: 'rgba(214,168,122,0.55)',
                      }}
                      className="mb-5"
                    />

                    {/* Title */}
                    <h3
                      className="text-ink"
                      style={{
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: '26px',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        letterSpacing: '-0.005em',
                      }}
                    >
                      {pillar.title}
                    </h3>

                    {/* Description */}
                    <p
                      className="mt-3"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '15px',
                        fontWeight: 300,
                        lineHeight: 1.65,
                        color: '#4A4F63',
                      }}
                    >
                      {pillar.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────  RECENT WORK  ───────────────────────── */}
      <section
        className="bg-white"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="section-recent"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div>
              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '9pt',
                  fontWeight: 600,
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                }}
                className="mb-5"
                data-testid="eyebrow-recent"
              >
                {t('home.recent.eyebrow')}
              </div>
              <h2
                className="text-ink"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: 'clamp(34px, 3.6vw, 50px)',
                  lineHeight: 1.08,
                  fontWeight: 500,
                  letterSpacing: '-0.005em',
                  maxWidth: '20ch',
                }}
              >
                {t('home.recent.headline1')}{' '}
                <em
                  className="italic"
                  style={{ color: '#B98556', fontWeight: 400 }}
                >
                  {t('home.recent.headline2')}
                </em>
              </h2>
            </div>

            {/* Filter chips */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="recent-filters">
              {FILTERS.map((f, i) => {
                const isActive = activeFilter === f;
                return (
                  <React.Fragment key={f}>
                    <button
                      type="button"
                      onClick={() => setActiveFilter(f)}
                      data-testid={`filter-${f.toLowerCase()}`}
                      className="transition-colors"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '10pt',
                        fontWeight: 600,
                        letterSpacing: '0.28em',
                        textTransform: 'uppercase',
                        color: isActive ? '#D6A87A' : '#0B0F1F',
                        borderBottom: isActive
                          ? '1px solid #D6A87A'
                          : '1px solid transparent',
                        paddingBottom: '4px',
                        background: 'transparent',
                      }}
                    >
                      {t(`filters.${f.toLowerCase()}`)}
                    </button>
                    {i < FILTERS.length - 1 && (
                      <span
                        aria-hidden="true"
                        style={{
                          color: 'rgba(214,168,122,0.45)',
                          fontSize: '12pt',
                        }}
                      >
                        ·
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Cases grid (no cards, no shadows) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {visibleCases.map((item, index) => (
              <article
                key={item.title}
                className="group"
                data-testid={`case-${index + 1}`}
              >
                {/* Photo with copper category chip + navy bottom overlay */}
                <div className="relative overflow-hidden" style={{ aspectRatio: '4/5' }}>
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                  />
                  {/* Navy bottom overlay (rgba(10,19,47,0) → rgba(10,19,47,0.85)) */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to bottom, rgba(10,19,47,0) 60%, rgba(10,19,47,0.85) 100%)',
                    }}
                  />
                  {/* Category chip */}
                  <div
                    className="absolute top-5 left-5"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                      border: '1px solid rgba(214,168,122,0.6)',
                      padding: '6px 12px',
                      backgroundColor: 'rgba(10,19,47,0.45)',
                    }}
                    data-testid={`case-${index + 1}-chip`}
                  >
                    {t(`filters.${item.category.toLowerCase()}`)}
                  </div>
                </div>

                {/* Caption */}
                <div className="mt-6">
                  <div
                    className="mb-2"
                    style={{
                      height: '1px',
                      width: '32px',
                      backgroundColor: 'rgba(214,168,122,0.55)',
                    }}
                  />
                  <h3
                    className="text-ink"
                    style={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: '26px',
                      fontWeight: 500,
                      lineHeight: 1.2,
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="mt-2"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      fontWeight: 300,
                      lineHeight: 1.6,
                      color: '#4A4F63',
                    }}
                  >
                    {item.desc}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {/* Inline link to full portfolio */}
          <div className="mt-16 flex justify-center">
            <Link
              to="/portfolio"
              data-testid="recent-portfolio-link"
              className="inline-flex items-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '10pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#0B0F1F',
                borderBottom: '1px solid rgba(214,168,122,0.55)',
                paddingBottom: '6px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#D6A87A';
                e.currentTarget.style.borderBottomColor = '#D6A87A';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#0B0F1F';
                e.currentTarget.style.borderBottomColor = 'rgba(214,168,122,0.55)';
              }}
            >
              {t('cta.viewPortfolio')}
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────────────────────  TESTIMONIALS  ───────────────────────── */}
      <section
        className="bg-bone"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="section-testimonials"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '9pt',
                fontWeight: 600,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: '#D6A87A',
              }}
              className="mb-5"
              data-testid="eyebrow-testimonials"
            >
              {t('home.testimonials.eyebrow')}
            </div>
            <h2
              className="text-ink"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(34px, 3.6vw, 50px)',
                lineHeight: 1.1,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              {t('home.testimonials.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#B98556', fontWeight: 400 }}
              >
                {t('home.testimonials.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-x-12 gap-y-14">
            {testimonials.map((tm, index) => (
              <figure
                key={tm.author}
                className="relative pt-2"
                data-testid={`testimonial-${index + 1}`}
              >
                {/* Open-quote glyph */}
                <div
                  aria-hidden="true"
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    fontSize: '88px',
                    lineHeight: 0.8,
                    color: '#D6A87A',
                  }}
                  className="mb-2 select-none"
                >
                  “
                </div>

                <blockquote
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    fontSize: '22px',
                    lineHeight: 1.5,
                    color: '#0B0F1F',
                  }}
                >
                  {tm.quote}
                </blockquote>

                {/* Copper hairline */}
                <div
                  className="mt-8 mb-5"
                  style={{
                    height: '1px',
                    width: '64px',
                    backgroundColor: 'rgba(214,168,122,0.55)',
                  }}
                />

                <figcaption>
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#0B0F1F',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {tm.author}
                  </div>
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#4A4F63',
                      letterSpacing: '0.04em',
                      marginTop: '2px',
                    }}
                  >
                    {tm.company}
                  </div>

                  {/* "WE DELIVERED" eyebrow + outcome */}
                  <div
                    className="mt-6"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.32em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                    }}
                    data-testid={`testimonial-${index + 1}-delivered`}
                  >
                    {t('home.testimonials.weDelivered')}
                  </div>
                  <div
                    className="mt-2"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      fontWeight: 400,
                      color: '#0B0F1F',
                      lineHeight: 1.55,
                    }}
                  >
                    {tm.delivered}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────  FINAL CTA  ───────────────────────── */}
      <section
        className="bg-navy text-bone relative overflow-hidden"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="section-final-cta"
      >
        {/* Subtle copper hairline at top */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2"
          style={{
            height: '1px',
            width: '160px',
            backgroundColor: 'rgba(214,168,122,0.55)',
          }}
        />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '9pt',
              fontWeight: 600,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: '#D6A87A',
            }}
            className="mb-6"
            data-testid="eyebrow-final"
          >
            {t('home.finalCta.eyebrow')}
          </div>

          <h2
            className="text-bone"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(40px, 4.2vw, 60px)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.005em',
            }}
          >
            {t('home.finalCta.headline1')}{' '}
            <em
              className="italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t('home.finalCta.headline2')}
            </em>
          </h2>

          <p
            className="mt-6"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '16pt',
              color: '#D6A87A',
              lineHeight: 1.5,
            }}
          >
            {t('home.finalCta.sub')}
          </p>

          {/* Single primary CTA, copper-outline pill */}
          <div className="mt-10 flex justify-center">
            <Link
              to="/contact"
              data-testid="final-cta-button"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t('cta.bookConsultation')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
